import { ExternalLink, Landmark, MapPin } from 'lucide-react';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import { ACADEMIC_LEDE, ACADEMIC_PARTNERS } from '@/data/partners';

/**
 * AcademicPartners (/partners — section 1) — "Universities that share our
 * ambition."
 *
 * ACADEMIC_LEDE frames the collaboration, then ACADEMIC_PARTNERS render as a
 * ScrollReveal-staggered responsive grid of plain hover-lift cards: a Landmark
 * icon in a navy chip, the partner name, a muted location row with a MapPin
 * glyph, the source description, and — pinned to the card foot — the partner
 * website as an outbound external link.
 *
 * Motion: SplitReveal headline, ScrollReveal child-staggers. Reduced-motion
 * safe; every institutional fact comes from '@/data/partners', nothing is
 * invented.
 */
export function AcademicPartners() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Academic Partners
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Universities that share our ambition.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              {ACADEMIC_LEDE}
            </p>
          </ScrollReveal>
        </header>

        {/* ── Partners grid ────────────────────────────────────────────── */}
        <ScrollReveal
          stagger={0.06}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {ACADEMIC_PARTNERS.map((partner) => (
            <div
              key={partner.name}
              className="flex h-full flex-col rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                <Landmark aria-hidden className="size-6" />
              </div>
              <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                {partner.name}
              </h3>
              <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin aria-hidden className="size-4 shrink-0" />
                {partner.location}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {partner.description}
              </p>
              <div className="mt-auto pt-4">
                <a
                  href={partner.href}
                  target="_blank"
                  rel="noreferrer"
                  data-cursor
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-navy underline decoration-2 decoration-transparent underline-offset-4 transition-colors duration-300 hover:decoration-brand-gold"
                >
                  {partner.website}
                  <ExternalLink aria-hidden className="size-4 shrink-0 text-brand-gold" />
                </a>
              </div>
            </div>
          ))}
        </ScrollReveal>
      </div>
    </section>
  );
}
